/**
 * Fletcher's evidence pack: the case's whole record of one check.
 *
 * It fits the narrow sheet as well as the expanded one (YAG-1078): the
 * recording sits on top at the pack's width, the steps of the tour of what
 * changed are a vertical list (`QaSteps`: the small screenshot that opens
 * large, held or did not hold, the check in words, Watch from), and every
 * other screenshot wraps below rather than scrolling sideways.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { qaStepsOf, qaVideoOf, type QaEvidence, type QaReport } from "@yagni/shared";
import { Pane, StatusTag, type StatusTagKind } from "../ui/index.js";
import { QaSteps, qaCaptureExpired, useQaVideoSeek } from "./QaSteps.js";
import "./fletcherCheck.css";
export type { QaReport } from "@yagni/shared";
type EvidenceProps = {
  report: QaReport;
  headSha?: string | null;
  selection?: { id: string; nonce: number };
};
export function QaEvidencePack({ report, headSha, selection }: EvidenceProps) {
  const identity = JSON.stringify([
    report.environmentRevisionId,
    report.runnerVersion,
    report.captures.map((capture) => capture.id),
  ]);
  return (
    <QaEvidencePackContent key={identity} report={report} headSha={headSha} selection={selection} />
  );
}
const OBJECTIVE_STATUS = {
  not_checked: "Not checked",
  investigating: "Partially checked",
  verified: "Verified",
  findings: "Findings",
} as const;
/** The outcome stamp: green only when it passed; everything else is the product telling on itself. */
function outcomeKind(outcome: QaReport["outcome"]): StatusTagKind {
  return outcome === "passed" ? "resolved" : "stuck";
}
function QaEvidencePackContent({ report, headSha, selection }: EvidenceProps) {
  const screenshots = report.captures.filter((c) => c.kind === "screenshot"),
    video = qaVideoOf(report);
  // The tour's steps carry their own screenshots; the viewer below keeps the rest.
  const tourSteps = useMemo(() => qaStepsOf(report), [report]);
  const inSteps = new Set(tourSteps.map((step) => step.screenshot?.id).filter(Boolean));
  const loose = screenshots.filter((capture) => !inSteps.has(capture.id));
  const [selected, setSelected] = useState(loose[0]),
    [failed, setFailed] = useState(false),
    [failedImage, setFailedImage] = useState<string | null>(null);
  const { player, seek, onLoadedMetadata } = useQaVideoSeek();
  const recording = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const toRecording = () =>
    recording.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  const show = (capture: QaEvidence, timestamp = capture.timestampSeconds) => {
    setSelected(capture);
    seek(timestamp);
    stage.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    stage.current?.focus({ preventScroll: true });
  };
  useEffect(() => {
    const capture = screenshots.find((item) => item.id === selection?.id);
    if (!capture) return;
    setSelected(capture);
    seek(capture.timestampSeconds);
    stage.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    stage.current?.focus({ preventScroll: true });
  }, [selection]);
  const expired = (capture: QaEvidence) => qaCaptureExpired(capture);
  const playable = video != null && !expired(video);
  const verified = report.investigation?.objectives.filter((o) => o.status === "verified").length;
  return (
    <Pane material="printed" className="qa-pack" aria-label="Fletcher visual evidence">
      <div className="qa-pack-head">
        <h2 className="sr-only">{report.outcome}</h2>
        <StatusTag kind={outcomeKind(report.outcome)} aria-hidden="true">
          {report.outcome}
        </StatusTag>
        <span className="qa-pack-fact">
          {report.checks} executed checks
          {headSha ? ` · Tested commit ${headSha.slice(0, 8)}` : ""}
        </span>
      </div>
      <div ref={recording} className="qa-video">
        {playable ? (
          <>
            <video
              ref={player}
              controls
              preload="metadata"
              src={video.url}
              aria-label="PR verification recording"
              onError={() => setFailed(true)}
              onLoadedMetadata={onLoadedMetadata}
            />
            {failed && (
              <p role="status" className="qa-stage-line">
                The recording could not be loaded. Refresh to check current access.
              </p>
            )}
          </>
        ) : (
          <p className="qa-stage-line">
            {video ? "The recording has expired." : "No recording was captured."} The written report
            remains available.
          </p>
        )}
        {report.recording && (
          <p className="qa-stage-line" role={report.recording.limited ? "status" : undefined}>
            {report.recording.limited ? "Recording limit reached · " : "Continuous video · "}
            {Math.round(report.recording.maxSeconds / 60)} minute limit ·{" "}
            {Math.round(report.recording.maxBytes / 1048576)} MB limit ·{" "}
            {((report.storageBytes ?? 0) / 1048576).toFixed(1)} MB of evidence retained
          </p>
        )}
      </div>
      {report.changeTour?.ran &&
        (report.changeTour.steps.length > 0 || report.changeTour.screens.length > 0) && (
          <section aria-label="What the video checks" className="wrc-sect">
            <h3 className="wrc-sect-l">What the video checks</h3>
            {report.changeTour.screens.length > 0 && (
              <p className="qa-card-note">
                Where the change shows:{" "}
                {report.changeTour.screens.map((screen) => screen.screen).join(", ")}
              </p>
            )}
            <QaSteps
              steps={tourSteps}
              {...(playable
                ? {
                    onWatch: (seconds: number) => {
                      seek(seconds);
                      toRecording();
                    },
                  }
                : {})}
            />
          </section>
        )}
      {!!report.remoteDependencies?.length && (
        <section aria-label="Remote dependency versions" className="wrc-sect">
          <h3 className="wrc-sect-l">Remote dependency versions</h3>
          <p className="qa-card-note">
            Deployment metadata is reported by the preview publisher. A tooling checkout does not
            prove which dependency is running remotely.
          </p>
          <ul className="qa-pack-grid">
            {report.remoteDependencies.map((dependency) => (
              <li key={dependency.repoId} className="qa-card">
                <h4 className="qa-card-title">{dependency.repository}</h4>
                <p className="qa-card-body">
                  {dependency.deployedSha
                    ? `Reported deployed commit ${dependency.deployedSha.slice(0, 8)}`
                    : "Deployed version unknown"}
                </p>
                <p className="qa-card-note">
                  Tooling checkout {dependency.checkoutSha.slice(0, 8)}
                </p>
                <details>
                  <summary>Full commits</summary>
                  <p>Checkout: {dependency.checkoutSha}</p>
                  {dependency.deployedSha && <p>Reported deployment: {dependency.deployedSha}</p>}
                </details>
              </li>
            ))}
          </ul>
        </section>
      )}
      {report.investigation && (
        <section aria-label="Investigation coverage" className="wrc-sect">
          <h3 className="wrc-sect-l">Investigation coverage</h3>
          <p className="qa-pack-count">
            {verified} of {report.investigation.objectives.length} planned behaviors verified with
            browser assertions.
          </p>
          <ul className="qa-pack-grid">
            {report.investigation.objectives.map((objective) => (
              <li key={objective.id} className="qa-card" data-status={objective.status}>
                <span className="qa-card-state">
                  {objective.risk} priority · {OBJECTIVE_STATUS[objective.status]}
                </span>
                <h4 className="qa-card-title">{objective.behavior}</h4>
                <p className="qa-card-body">{objective.expected}</p>
                {objective.basis === "assumption" && (
                  <p className="qa-card-note">Expected behavior is an assumption to confirm.</p>
                )}
                {objective.screenshotIds.length > 0 && (
                  <button
                    type="button"
                    className="qa-card-verb"
                    onClick={() => {
                      const capture = screenshots.find(
                        (c) => c.id === objective.screenshotIds.at(-1),
                      );
                      if (capture) show(capture);
                    }}
                  >
                    View observed behavior
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <div
        ref={stage}
        tabIndex={-1}
        className="qa-stage"
        aria-label="Evidence viewer"
        hidden={!selected && loose.length === 0}
      >
        {selected && (
          <figure id={`qa-capture-${selected.id}`}>
            {expired(selected) ? (
              <p className="qa-stage-line">This screenshot has expired.</p>
            ) : failedImage === selected.id ? (
              <p role="status" className="qa-stage-line">
                The screenshot could not be loaded. Refresh to check current access.
              </p>
            ) : (
              <img
                src={selected.url}
                alt={selected.title}
                loading="lazy"
                onError={() => setFailedImage(selected.id)}
              />
            )}
            <figcaption>
              {selected.title} · {new Date(selected.capturedAt).toLocaleString()}
            </figcaption>
          </figure>
        )}
        {loose.length > 0 && (
          <div className="qa-thumbs" aria-label="Captured steps">
            {loose.map((capture) => (
              <button
                key={capture.id}
                type="button"
                className="qa-thumb"
                aria-label={capture.title}
                aria-pressed={selected?.id === capture.id}
                onClick={() => show(capture)}
              >
                {!expired(capture) && <img src={capture.url} alt="" loading="lazy" />}
                <span>{capture.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {report.flows?.length ? (
        <details className="qa-fold">
          <summary>Checked flows</summary>
          <ol>
            {report.flows.map((flow, i) => (
              <li key={i}>
                <strong>{flow.action}</strong>
                {flow.expected && <p>Expected: {flow.expected}</p>}
                <p>{flow.observed}</p>
                {flow.screenshotId && (
                  <button
                    type="button"
                    className="qa-card-verb"
                    onClick={() => {
                      const capture = screenshots.find((item) => item.id === flow.screenshotId);
                      if (capture) show(capture, flow.timestampSeconds ?? capture.timestampSeconds);
                    }}
                  >
                    View this step
                  </button>
                )}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
      {report.testResults?.length ? (
        <details className="qa-fold">
          <summary>Test results and diagnostics</summary>
          {report.testResults.map((test, index) => (
            <section key={index} className="min-w-0">
              <h3>{test.command}</h3>
              <p>
                {test.checks} reported passing checks
                {test.exitCode !== 0 ? " in a failed command" : ""} · Exit code {test.exitCode}
              </p>
              <pre>{test.output || "No output was captured."}</pre>
            </section>
          ))}
        </details>
      ) : null}
      {!!report.diagnostics?.length && (
        <details className="qa-fold">
          <summary>Browser diagnostics ({report.diagnostics.length})</summary>
          <p>
            Diagnostic events are supporting observations. Findings identify the user-visible
            behavior that failed.
          </p>
          <ul>
            {report.diagnostics.map((item, i) => (
              <li key={i}>
                {item.kind.replaceAll("_", " ")}
                {item.status ? ` · HTTP ${item.status}` : ""}
                {item.origin ? ` · ${item.origin}` : ""}
                {item.resourceType ? ` · ${item.resourceType}` : ""}
                {item.count ? ` · ${item.count} event(s)` : ""}
                {item.message && <pre>{item.message}</pre>}
              </li>
            ))}
          </ul>
        </details>
      )}
      <details className="qa-fold">
        <summary>Capture details and retention</summary>
        <p className="qa-fold-sub">
          Environment {report.environmentRevisionId ?? "not configured"}
          {headSha ? ` · Commit ${headSha}` : ""}
        </p>
        <p className="qa-fold-sub">
          Runner {report.runnerVersion}
          {report.deploymentId ? ` · GitHub deployment ${report.deploymentId}` : ""}
        </p>
        <ul>
          {report.captures.map((capture) => (
            <li key={capture.id}>
              {capture.kind} · {capture.title} · {expired(capture) ? "Expired" : "Expires"}{" "}
              {new Date(capture.expiresAt).toLocaleString()}
              <br />
              <span className="qa-fold-sub">SHA-256 {capture.sha256}</span>
            </li>
          ))}
        </ul>
      </details>
    </Pane>
  );
}
